/**
 * ModelingCurveChart — the kit primitive for an aged cohort curve (cumulative
 * credit-loss %, remaining-balance survival %, SMM/CPR by age, ...). It is a
 * recharts `ComposedChart` and is one of only TWO files allowed to import
 * recharts (kit-first rule); app code reaches for it via `~/components/ui`.
 *
 * What it draws, back-to-front:
 *  1. faint EXPOSURE bars on a secondary y-axis (how many loans back each age) —
 *     muted neutral, sitting behind everything so the eye reads the curve first;
 *  2. a confidence BAND — an Area between each point's `lower`/`upper`, filled in
 *     low-opacity brand teal (wider where the data is thin, which is honest);
 *  3. the main LINE, split into a SOLID observed segment and a DASHED
 *     extrapolated segment. The split is driven by each point's `observed` flag:
 *     we project the value onto two series keys (`observedValue` /
 *     `extrapolatedValue`) that overlap by exactly one point at the boundary so
 *     the two segments meet seamlessly;
 *  4. a vertical REFERENCE marker at the observed→extrapolated boundary ("data
 *     ends here");
 *  5. an optional `secondarySeries` (e.g. a contractual line) drawn without a
 *     band, for behavioral-vs-contractual comparisons.
 *
 * Per-point TOOLTIP shows age (months + years), value, band, and exposure n.
 *
 * SSR: wrapped in `ChartFrame`, which reserves a fixed-height box and only mounts
 * the chart on the client (recharts' ResponsiveContainer needs a real width).
 * Colors resolve from design-system CSS vars via `useChartColors` (dark-mode
 * safe; recharts can't read CSS custom properties for SVG paint).
 */
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame, TooltipSwatch, useChartColors } from './chart-internals';
import styles from './charts.module.css';

/** One aged point on the curve. Mirrors the engine's `CurvePoint` shape. */
export interface ModelingCurvePoint {
  readonly ageMonths: number;
  /** Curve value (meaning depends on the curve: cum-loss %, balance %, ...). */
  readonly value: number;
  /** Confidence band low (clamped). */
  readonly lower: number;
  /** Confidence band high (clamped). */
  readonly upper: number;
  /** Loans backing this age — drives the faint exposure bars. */
  readonly exposure: number;
  /** true = observed (solid); false = extrapolated (dashed). */
  readonly observed: boolean;
}

/** An optional comparison line drawn without a band (e.g. contractual). */
export interface ModelingCurveSecondarySeries {
  readonly key: string;
  readonly label: string;
  /** value keyed by ageMonths; points missing an entry are skipped. */
  readonly points: ReadonlyArray<{ readonly ageMonths: number; readonly value: number }>;
}

export interface ModelingCurveChartProps {
  readonly points: readonly ModelingCurvePoint[];
  /** Optional second line (no band), e.g. contractual vs behavioral survival. */
  readonly secondarySeries?: ModelingCurveSecondarySeries | undefined;
  /** Accessible name for the chart region. */
  readonly ariaLabel: string;
  /** Series label for the main line (legend + tooltip). */
  readonly valueLabel: string;
  readonly xAxisLabel?: string | undefined;
  readonly yAxisLabel?: string | undefined;
  /** Format the curve value for axis ticks + tooltip (e.g. percent, money). */
  readonly valueFormatter?: ((value: number) => string) | undefined;
  /** Format the exposure n (defaults to integer). */
  readonly exposureFormatter?: ((n: number) => string) | undefined;
  /**
   * Whether this series carries a real confidence band (F116/F180). Default `true`.
   * Charts that collapse `lower === upper` (the cash-flow runoff, the SMM/CPR-by-age
   * detail) pass `false` so the tooltip never renders a degenerate "$X to $X" band
   * row and the legend gains no band key.
   */
  readonly hasBand?: boolean | undefined;
  /**
   * Whether this series carries real per-age exposure (F116). Default `true`. Charts
   * that set `exposure: 0` on every point (runoff, SMM/CPR) pass `false` so the
   * tooltip never renders an "Exposure n: 0" row.
   */
  readonly hasExposure?: boolean | undefined;
  /** Reserved box height in px (CLS-safe; the chart fills it). Default 320. */
  readonly height?: number | undefined;
  readonly className?: string | undefined;
}

/** The i18n key for a band's statistic — observed CI vs projected fan (F180). */
export type BandLabelKey = 'charts.band95' | 'charts.bandProjected';

/** Which tooltip rows a point renders + how its band is labeled (F116 / F180). */
export interface CurveTooltipRowFlags {
  readonly showBand: boolean;
  readonly showExposure: boolean;
  readonly bandLabelKey: BandLabelKey;
}

/**
 * Decide which tooltip rows a point renders and how to label its band (F116 / F180).
 *
 *  - **Band row** shows only when the chart carries a real band (`hasBand`) AND this
 *    point's band is non-degenerate (`lower !== upper`) — so the runoff + SMM/CPR
 *    charts (which collapse `lower === upper` and pass `hasBand: false`) never show a
 *    "$X to $X" row.
 *  - **Exposure row** shows only when the series carries real exposure (`hasExposure`)
 *    — the runoff + SMM/CPR charts set `exposure: 0` and pass `hasExposure: false`.
 *  - **Band label (F180)** distinguishes the two statistics that share the band's
 *    visual language: an OBSERVED point's band is a Greenwood/binomial ~95% interval;
 *    an extrapolated point's band is a heuristic projection fan — never labeled the
 *    same. `observed` is lane Q's band-KIND discriminator (observed CI vs projected
 *    fan); an explicit per-point band-kind, if it lands, maps 1:1 to this flag.
 */
export function curveTooltipRowFlags(
  row: { readonly observed: boolean; readonly lower: number; readonly upper: number },
  opts: { readonly hasBand: boolean; readonly hasExposure: boolean },
): CurveTooltipRowFlags {
  return {
    showBand: opts.hasBand && row.lower !== row.upper,
    showExposure: opts.hasExposure,
    bandLabelKey: row.observed ? 'charts.band95' : 'charts.bandProjected',
  };
}

/** A single merged row recharts consumes (per age). */
interface CurveRow {
  ageMonths: number;
  observedValue: number | null;
  extrapolatedValue: number | null;
  band: [number, number] | null;
  exposure: number;
  rawValue: number;
  lower: number;
  upper: number;
  observed: boolean;
  secondaryValue: number | null;
}

function defaultValueFormatter(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function defaultExposureFormatter(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** "18 mo (1.5 yr)" — age in months with the years equivalent. */
function formatAge(t: TFunction, ageMonths: number): string {
  const years = (ageMonths / 12).toLocaleString('en-US', { maximumFractionDigits: 1 });
  return t('charts.age', { months: ageMonths, years });
}

/**
 * Project points onto two overlapping line keys so the solid (observed) and
 * dashed (extrapolated) segments meet at the boundary point. The boundary point
 * carries BOTH keys; observed-only points carry only `observedValue`,
 * extrapolated-only points carry only `extrapolatedValue`.
 */
function buildRows(
  points: readonly ModelingCurvePoint[],
  secondary: ModelingCurveSecondarySeries | undefined,
): { rows: CurveRow[]; boundaryAge: number | null } {
  const lastObservedIndex = points.reduce(
    (acc, p, i) => (p.observed ? i : acc),
    -1,
  );
  const boundaryAge =
    lastObservedIndex >= 0 && lastObservedIndex < points.length - 1
      ? points[lastObservedIndex]!.ageMonths
      : null;

  const secondaryByAge = new Map<number, number>();
  secondary?.points.forEach((p) => secondaryByAge.set(p.ageMonths, p.value));

  const rows = points.map((p, i): CurveRow => {
    const isBoundary = i === lastObservedIndex && boundaryAge !== null;
    return {
      ageMonths: p.ageMonths,
      // Observed line carries the value through the last observed point.
      observedValue: p.observed ? p.value : null,
      // Extrapolated line starts AT the boundary (so the two segments touch).
      extrapolatedValue: !p.observed || isBoundary ? p.value : null,
      band: [p.lower, p.upper],
      exposure: p.exposure,
      rawValue: p.value,
      lower: p.lower,
      upper: p.upper,
      observed: p.observed,
      secondaryValue: secondaryByAge.has(p.ageMonths)
        ? secondaryByAge.get(p.ageMonths)!
        : null,
    };
  });
  return { rows, boundaryAge };
}

export function ModelingCurveChart({
  points,
  secondarySeries,
  ariaLabel,
  valueLabel,
  xAxisLabel,
  yAxisLabel,
  valueFormatter = defaultValueFormatter,
  exposureFormatter = defaultExposureFormatter,
  hasBand = true,
  hasExposure = true,
  height = 320,
  className,
}: ModelingCurveChartProps): ReactNode {
  const { t } = useTranslation();
  const colors = useChartColors();
  const { rows, boundaryAge } = useMemo(
    () => buildRows(points, secondarySeries),
    [points, secondarySeries],
  );

  const hasExtrapolation = rows.some((r) => !r.observed);

  return (
    <div className={className}>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.swatchLine} style={{ borderTopColor: colors.primary }} aria-hidden />
          {valueLabel}
        </span>
        {hasExtrapolation ? (
          <span className={styles.legendItem}>
            <span className={styles.swatchDashed} style={{ borderTopColor: colors.accent }} aria-hidden />
            {t('charts.projectedSuffix', { label: valueLabel })}
          </span>
        ) : null}
        {/* The shaded band, named by its statistic (F180): the observed region is a
            ~95% interval; the projected region (when present) is a heuristic fan —
            never the same statistic. Band-less charts (runoff, SMM/CPR) skip this. */}
        {hasBand ? (
          <span className={styles.legendItem}>
            <TooltipSwatch color={colors.bandBg} />
            {t('charts.band95')}
          </span>
        ) : null}
        {hasBand && hasExtrapolation ? (
          <span className={styles.legendItem}>
            <TooltipSwatch color={colors.bandBg} />
            {t('charts.bandProjected')}
          </span>
        ) : null}
        {secondarySeries ? (
          <span className={styles.legendItem}>
            <span
              className={styles.swatchLine}
              style={{ borderTopColor: colors.axis }}
              aria-hidden
            />
            {secondarySeries.label}
          </span>
        ) : null}
      </div>

      <ChartFrame height={height} ariaLabel={ariaLabel}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: xAxisLabel ? 24 : 8, left: yAxisLabel ? 16 : 0 }}>
            <CartesianGrid stroke={colors.grid} vertical={false} />
            <XAxis
              dataKey="ageMonths"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fill: colors.axis, fontSize: 11 }}
              tickLine={{ stroke: colors.grid }}
              axisLine={{ stroke: colors.grid }}
              {...(xAxisLabel
                ? { label: { value: xAxisLabel, position: 'insideBottom', offset: -12, fill: colors.axisLabel, fontSize: 12 } }
                : {})}
            />
            {/* Primary y-axis: the curve value. */}
            <YAxis
              yAxisId="value"
              tick={{ fill: colors.axis, fontSize: 11 }}
              tickLine={{ stroke: colors.grid }}
              axisLine={{ stroke: colors.grid }}
              tickFormatter={(v: number) => valueFormatter(v)}
              {...(yAxisLabel
                ? { label: { value: yAxisLabel, angle: -90, position: 'insideLeft', fill: colors.axisLabel, fontSize: 12, style: { textAnchor: 'middle' } } }
                : {})}
            />
            {/* Secondary y-axis: exposure n, hidden ticks (context, not focus). */}
            <YAxis yAxisId="exposure" orientation="right" hide />

            {/* (1) Exposure bars — behind everything. */}
            <Bar
              yAxisId="exposure"
              dataKey="exposure"
              fill={colors.exposureBar}
              isAnimationActive={false}
              barSize={6}
            />

            {/* (2) Confidence band — Area between lower/upper. */}
            <Area
              yAxisId="value"
              dataKey="band"
              stroke="none"
              fill={colors.bandBg}
              isAnimationActive={false}
              connectNulls
              activeDot={false}
              legendType="none"
            />

            {/* (5) Optional secondary line (no band). */}
            {secondarySeries ? (
              <Line
                yAxisId="value"
                dataKey="secondaryValue"
                stroke={colors.axis}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            ) : null}

            {/* (3a) Observed line — solid. */}
            <Line
              yAxisId="value"
              dataKey="observedValue"
              stroke={colors.primary}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
            {/* (3b) Extrapolated line — dashed. */}
            <Line
              yAxisId="value"
              dataKey="extrapolatedValue"
              stroke={colors.accent}
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />

            {/* (4) Boundary marker — where observation ends. */}
            {boundaryAge !== null ? (
              <ReferenceLine
                yAxisId="value"
                x={boundaryAge}
                stroke={colors.reference}
                strokeDasharray="3 3"
              />
            ) : null}

            <Tooltip
              cursor={{ stroke: colors.reference, strokeWidth: 1 }}
              isAnimationActive={false}
              content={
                <CurveTooltip
                  t={t}
                  valueLabel={valueLabel}
                  valueFormatter={valueFormatter}
                  exposureFormatter={exposureFormatter}
                  secondaryLabel={secondarySeries?.label}
                  colors={colors}
                  hasBand={hasBand}
                  hasExposure={hasExposure}
                />
              }
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartFrame>
    </div>
  );
}

/** The active payload entry recharts hands a custom tooltip (loosely typed). */
interface TooltipEntry {
  readonly payload?: CurveRow;
}
interface CurveTooltipProps {
  readonly active?: boolean;
  readonly payload?: readonly TooltipEntry[];
  readonly t: TFunction;
  readonly valueLabel: string;
  readonly valueFormatter: (value: number) => string;
  readonly exposureFormatter: (n: number) => string;
  readonly secondaryLabel?: string | undefined;
  readonly colors: ReturnType<typeof useChartColors>;
  readonly hasBand: boolean;
  readonly hasExposure: boolean;
}

function CurveTooltip({
  active,
  payload,
  t,
  valueLabel,
  valueFormatter,
  exposureFormatter,
  secondaryLabel,
  colors,
  hasBand,
  hasExposure,
}: CurveTooltipProps): ReactNode {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const flags = curveTooltipRowFlags(row, { hasBand, hasExposure });
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipLabel}>{formatAge(t, row.ageMonths)}</span>
      <div className={styles.tooltipRow}>
        <span className={styles.tooltipKey}>
          <TooltipSwatch color={row.observed ? colors.primary : colors.accent} />
          {row.observed ? valueLabel : t('charts.projectedSuffix', { label: valueLabel })}
        </span>
        <span className={styles.tooltipValue}>{valueFormatter(row.rawValue)}</span>
      </div>
      {row.secondaryValue !== null && secondaryLabel ? (
        <div className={styles.tooltipRow}>
          <span className={styles.tooltipKey}>
            <TooltipSwatch color={colors.axis} />
            {secondaryLabel}
          </span>
          <span className={styles.tooltipValue}>{valueFormatter(row.secondaryValue)}</span>
        </div>
      ) : null}
      {flags.showBand ? (
        <div className={styles.tooltipRow}>
          <span className={styles.tooltipKey}>{t(flags.bandLabelKey)}</span>
          <span className={styles.tooltipValue}>
            {t('charts.bandRange', {
              lower: valueFormatter(row.lower),
              upper: valueFormatter(row.upper),
            })}
          </span>
        </div>
      ) : null}
      {flags.showExposure ? (
        <div className={styles.tooltipRow}>
          <span className={styles.tooltipKey}>
            <TooltipSwatch color={colors.exposureBar} />
            {t('charts.exposure')}
          </span>
          <span className={styles.tooltipValue}>{exposureFormatter(row.exposure)}</span>
        </div>
      ) : null}
    </div>
  );
}
